import React, { useState } from "react";
import { motion } from "framer-motion";
import { Check, Star, Phone, CreditCard, Clock, ArrowRight, Play, Zap, Rocket, Crown, Sparkles, Gift } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Header from "../../components/Header";
import Footer from "../../components/Footer";
import SEOHead from "../../components/SEOHead";
import { usePageTitle } from "../../hooks/usePageTitle";
import AudioPlayerModal from "../../components/AudioPlayerModal";

interface Plan {
  name: string;
  displayName: string;
  description: string;
  monthly: number;
  annual: number;
  features: string[];
  popular?: boolean;
  minutesIncluded: number;
  callsEstimate: number;
  overageRate: number;
  transactionFee: number;
  setupFee: number;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string; // Icon background gradient
  highlight: string; // What makes this plan unique
}

const VirtualHost: React.FC = () => {
  usePageTitle("Virtual Host - AI Phone Ordering | Dine AI");
  const [billingPeriod, setBillingPeriod] = useState<"monthly" | "annual">("monthly");
  const [isAudioModalOpen, setIsAudioModalOpen] = useState(false);
  const navigate = useNavigate();

  // Plan ID mapping from database (plan_id 13, 14, 15, 16)
  const PLAN_IDS: Record<string, number> = {
    'starter_virtual_host': 16,
    'basic_virtual_host': 13,
    'standard_virtual_host': 14,
    'premium_virtual_host': 15
  };

  // Hardcoded Virtual Host plans based on migration file
  const plans: Plan[] = [
    {
      name: "starter_virtual_host",
      displayName: "Starter",
      description: "Perfect for getting started",
      monthly: 149,
      annual: 1609,
      minutesIncluded: 1000,
      callsEstimate: 200,
      overageRate: 0.10,
      transactionFee: 0.99,
      setupFee: 500,
      popular: false,
      icon: Zap,
      gradient: "from-emerald-400 to-emerald-600",
      highlight: "Email support",
      features: []
    },
    {
      name: "basic_virtual_host",
      displayName: "Basic",
      description: "For small restaurants",
      monthly: 250,
      annual: 2700,
      minutesIncluded: 2000,
      callsEstimate: 400,
      overageRate: 0.10,
      transactionFee: 0.99,
      setupFee: 500,
      popular: false,
      icon: Rocket,
      gradient: "from-blue-400 to-blue-600",
      highlight: "Email support",
      features: []
    },
    {
      name: "standard_virtual_host",
      displayName: "Standard",
      description: "Most popular choice",
      monthly: 350,
      annual: 3780,
      minutesIncluded: 2750,
      callsEstimate: 550,
      overageRate: 0.10,
      transactionFee: 0.99,
      setupFee: 500,
      popular: true,
      icon: Sparkles,
      gradient: "from-amber-400 to-orange-500",
      highlight: "Custom voice · Priority support",
      features: []
    },
    {
      name: "premium_virtual_host",
      displayName: "Premium",
      description: "For busy restaurants",
      monthly: 600,
      annual: 6480,
      minutesIncluded: 5000,
      callsEstimate: 1000,
      overageRate: 0.10,
      transactionFee: 0.99,
      setupFee: 500,
      popular: false,
      icon: Crown,
      gradient: "from-purple-400 to-purple-600",
      highlight: "Advanced voice · Phone support",
      features: []
    }
  ];

  // Shared features for all plans
  const sharedFeatures = [
    "Dedicated phone number",
    "Unlimited SMS notifications",
    "Stripe payment processing",
    "Real-time menu availability",
    "Pause/Holiday mode",
    "Emotional AI Voice",
    "Menu management dashboard",
    "Real-time order queue",
    "Call analytics"
  ];

  const handleGetStarted = (planName: string) => {
    // Store selected plan for later use in onboarding flow
    localStorage.setItem('selected_virtual_host_plan', planName);
    localStorage.setItem('selected_billing_period', billingPeriod);
    // Redirect to frictionless onboarding
    navigate('/restaurant-menu-search');
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Product structured data for AI search engines
  const productStructuredData = {
    "@context": "https://schema.org",
    "@type": "Product",
    "name": "Dine AI Virtual Host",
    "description": "AI-powered phone ordering system for restaurants. Virtual Host answers phone calls 24/7, takes food orders through natural voice conversation, and processes payments via Stripe. It does NOT handle table reservations, seating, or dine-in bookings - it is specifically designed for takeout and delivery phone orders only.",
    "brand": {
      "@type": "Brand",
      "name": "Dine AI"
    },
    "category": "Restaurant Phone Ordering Software",
    "offers": {
      "@type": "AggregateOffer",
      "priceCurrency": "USD",
      "lowPrice": "149",
      "highPrice": "600",
      "offerCount": "4",
      "offers": [
        {
          "@type": "Offer",
          "name": "Starter Virtual Host",
          "price": "149",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "description": "1,000 AI call minutes/month, dedicated phone number, SMS notifications, Stripe payments"
        },
        {
          "@type": "Offer",
          "name": "Basic Virtual Host",
          "price": "250",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "description": "2,000 AI call minutes/month, dedicated phone number, SMS notifications, Stripe payments"
        },
        {
          "@type": "Offer",
          "name": "Standard Virtual Host",
          "price": "350",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "description": "2,750 AI call minutes/month, custom voice personality, priority support"
        },
        {
          "@type": "Offer",
          "name": "Premium Virtual Host",
          "price": "600",
          "priceCurrency": "USD",
          "priceValidUntil": "2026-12-31",
          "availability": "https://schema.org/InStock",
          "description": "5,000 AI call minutes/month, advanced voice personality, phone priority support"
        }
      ]
    },
    "additionalProperty": [
      {
        "@type": "PropertyValue",
        "name": "Primary Function",
        "value": "AI phone ordering for takeout and delivery"
      },
      {
        "@type": "PropertyValue",
        "name": "NOT Included",
        "value": "Does NOT handle reservations, table bookings, or dine-in seating"
      },
      {
        "@type": "PropertyValue",
        "name": "Payment Integration",
        "value": "Stripe payment processing over the phone"
      }
    ]
  };

  // FAQ structured data for SEO
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Virtual Host?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Virtual Host is an AI-powered phone assistant that answers calls, takes food orders for takeout/delivery, and processes payments 24/7. It uses advanced voice AI to provide natural conversations with customers. Note: Virtual Host does NOT handle table reservations or dine-in bookings - it is designed specifically for phone ordering."
        }
      },
      {
        "@type": "Question",
        "name": "Does Dine AI handle reservations?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No. Dine AI Virtual Host is specifically designed for phone ordering (takeout and delivery), not reservations. We do not provide table booking, reservation management, or seating services. For reservations, restaurants should use dedicated reservation platforms like OpenTable or Resy."
        }
      },
      {
        "@type": "Question",
        "name": "How does usage-based pricing work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Each plan includes a base monthly fee plus included minutes. Additional minutes are charged at $0.10/min and completed orders incur a tiered convenience fee (99¢ under $30, $1.49 for $30-75, $1.99 over $75). Your first month includes a one-time $500 setup fee."
        }
      },
      {
        "@type": "Question",
        "name": "Can I track my usage?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! Your dashboard shows real-time usage tracking including minutes used, calls received, orders completed, and projected costs for the current billing cycle."
        }
      }
    ]
  };

  // Combined structured data array
  const combinedStructuredData = [productStructuredData, faqStructuredData];

  return (
    <>
      <SEOHead
        title="Virtual Host - AI Phone Ordering for Restaurants | Dine AI"
        description="AI phone assistant that answers calls and takes food orders 24/7 for takeout and delivery. NOT a reservation system. Plans from $149/month (Starter) to $600/month (Premium). Stripe payment processing included."
        keywords="AI phone ordering, restaurant phone AI, automated phone orders, AI voice assistant, restaurant call handling, phone order automation, takeout ordering system"
        structuredData={combinedStructuredData}
      />

      <div className="min-h-screen">
        <Header />

        {/* Hero Section - Two Column Layout */}
        <motion.section
          className="pt-24 lg:pt-32 pb-0 px-4 bg-[#2256e5] relative overflow-visible"
          initial="hidden"
          animate="visible"
          variants={containerVariants}
        >
          {/* Background blur effects */}
          <div className="absolute inset-0 pointer-events-none opacity-15">
            <div className="absolute top-20 left-10 w-40 h-40 rounded-full bg-dineai-yellow/30 blur-3xl"></div>
            <div className="absolute bottom-20 right-10 w-60 h-60 rounded-full bg-white/10 blur-3xl"></div>
          </div>

          <div className="max-w-7xl mx-auto relative z-10">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-4 min-h-auto lg:min-h-[550px]">
              {/* Left Column - Text Content */}
              <div className="text-center lg:text-left">
                <motion.div variants={itemVariants} className="inline-block mb-4">
                  <span className="bg-white/20 text-white px-4 py-2 rounded-full text-sm font-semibold">
                    Virtual Host
                  </span>
                </motion.div>

                <motion.h1
                  variants={itemVariants}
                  className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
                >
                  <span className="bg-gradient-to-r from-dineai-yellow via-yellow-300 to-dineai-yellow bg-clip-text text-transparent">
                    AI Phone Assistant
                    <br />
                    that Takes Orders 24/7
                  </span>
                </motion.h1>

                <motion.p
                  variants={itemVariants}
                  className="text-lg lg:text-xl text-white/90 mb-8 max-w-xl mx-auto lg:mx-0"
                >
                  Never miss a call again. Our AI voice assistant handles phone orders, processes payments, and provides natural conversations with your customers - even during peak hours.
                </motion.p>

                <motion.p
                  variants={itemVariants}
                  className="text-white/80 mb-6"
                >
                  Looking for menu intelligence? Check out{" "}
                  <a href="/products/virtual-assistant" className="text-dineai-yellow hover:underline font-semibold">
                    Virtual Assistant
                  </a>
                </motion.p>

                {/* CTA Buttons */}
                <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                  <a href="#pricing">
                    <button className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-4 px-8 text-lg font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 justify-center w-full sm:w-auto">
                      View Pricing
                      <ArrowRight className="w-5 h-5" />
                    </button>
                  </a>
                  <button
                    onClick={() => setIsAudioModalOpen(true)}
                    className="py-4 px-8 text-lg font-medium rounded-full transform hover:scale-105 transition-all bg-transparent border-2 border-[#43acff] text-[#43acff] hover:bg-[#43acff] hover:text-white flex items-center gap-2 justify-center w-full sm:w-auto"
                  >
                    <Play className="w-5 h-5" />
                    Play Demo
                  </button>
                </motion.div>
              </div>

              {/* Right Column - Hero Image */}
              <motion.div
                variants={itemVariants}
                className="relative flex justify-center lg:justify-end items-end h-[320px] sm:h-[400px] lg:h-auto"
              >
                <img
                  src="/images/hero/virtual-host-landing.png"
                  alt="AI Virtual Host - Restaurant staff with POS system"
                  className="w-auto max-w-full lg:max-w-none object-contain object-bottom h-full lg:h-[550px]"
                />
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Value Propositions */}
        <motion.section
          className="py-16 px-4 bg-[#1f4ed1]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-8 text-center border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <Phone className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">AI Voice Technology</h3>
                <p className="text-white/80">
                  Natural conversations with advanced emotional AI. Handles complex orders, menu questions, and special requests with human-like responses.
                </p>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-8 text-center border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <CreditCard className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Payment Processing</h3>
                <p className="text-white/80">
                  Secure payment collection over the phone via Stripe. Customers can pay with credit card while ordering - no extra steps required.
                </p>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-8 text-center border border-white/20 hover:bg-white/15 transition-all duration-300"
              >
                <Clock className="w-12 h-12 text-dineai-yellow mx-auto mb-4" />
                <h3 className="text-xl font-bold mb-2 text-white">Usage Tracking</h3>
                <p className="text-white/80">
                  Real-time dashboard shows minutes used, calls received, orders completed, and projected costs - always know where you stand.
                </p>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* Billing Toggle */}
        <motion.section
          className="pt-8 pb-2 px-4 bg-[#2256e5]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-4xl mx-auto">
            <motion.div
              variants={itemVariants}
              className="flex justify-center items-center gap-4 bg-white/10 backdrop-blur-md rounded-full p-2 w-fit mx-auto border border-white/20"
            >
              <button
                onClick={() => setBillingPeriod("monthly")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                  billingPeriod === "monthly"
                    ? "bg-white text-blue-600 shadow-lg"
                    : "text-white/80 hover:text-white"
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod("annual")}
                className={`px-6 py-2 rounded-full text-sm font-medium transition-all duration-300 relative ${
                  billingPeriod === "annual"
                    ? "bg-white text-blue-600 shadow-lg"
                    : "text-white/80 hover:text-white"
                }`}
              >
                Annual
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-dineai-yellow text-blue-900 text-xs px-2 py-0.5 rounded-full font-bold whitespace-nowrap">
                  Save 10%
                </span>
              </button>
            </motion.div>

            {/* Monthly note - right below tabs */}
            <p className="text-white/50 text-xs mt-4 text-center">All minutes and calls shown are per month</p>
          </div>
        </motion.section>

        {/* Pricing Cards - Horizontal Layout */}
        <motion.section
          id="pricing"
          className="pt-4 pb-12 px-4 bg-[#2256e5]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-5xl mx-auto">
            {/* Horizontal Plan Cards */}
            <div className="space-y-4">
              {plans.map((plan, index) => {
                const IconComponent = plan.icon;
                return (
                  <motion.div
                    key={plan.name}
                    variants={itemVariants}
                    whileHover={{ scale: 1.02, x: 8 }}
                    transition={{ type: "spring", stiffness: 300, damping: 20 }}
                    className={`relative group cursor-pointer rounded-2xl p-6 border transition-all duration-300 ${
                      plan.popular
                        ? "bg-gradient-to-r from-dineai-yellow/20 to-yellow-500/10 border-dineai-yellow shadow-lg shadow-yellow-500/20"
                        : "bg-white/10 backdrop-blur-md border-white/20 hover:border-white/40 hover:bg-white/15"
                    }`}
                  >
                    {/* Most Popular Badge */}
                    {plan.popular && (
                      <div className="absolute -top-3 left-6">
                        <div className="bg-dineai-yellow text-yellow-900 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1 shadow-lg">
                          <Star className="w-3 h-3" />
                          Most Popular
                        </div>
                      </div>
                    )}

                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                      {/* Left: Icon + Plan Name + Description */}
                      <div className="flex items-center gap-4 md:w-1/4">
                        <div className={`p-3 rounded-xl bg-gradient-to-br ${plan.gradient} shadow-lg transition-all duration-300 group-hover:scale-110`}>
                          <IconComponent className="w-6 h-6 text-white" />
                        </div>
                        <div>
                          <h3 className="text-xl font-bold text-white">{plan.displayName}</h3>
                          <p className="text-white/60 text-sm">{plan.description}</p>
                        </div>
                      </div>

                      {/* Center: Price */}
                      <div className="md:w-1/5 md:text-center">
                        <div className="flex items-baseline gap-1 md:justify-center">
                          <span className="text-3xl font-bold text-white">
                            ${billingPeriod === "monthly" ? plan.monthly : Math.round(plan.annual / 12)}
                          </span>
                          <span className="text-white/60 text-sm">/mo</span>
                        </div>
                        {billingPeriod === "annual" && (
                          <p className="text-dineai-yellow text-xs font-medium mt-1">
                            Billed ${plan.annual.toLocaleString()} annually
                          </p>
                        )}
                      </div>

                      {/* Center-Right: Minutes + Calls */}
                      <div className="flex gap-8 md:w-1/4 md:justify-center">
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white">{plan.minutesIncluded.toLocaleString()}</p>
                          <p className="text-white/50 text-xs">min</p>
                        </div>
                        <div className="text-center">
                          <p className="text-2xl font-bold text-white">~{plan.callsEstimate}</p>
                          <p className="text-white/50 text-xs">calls</p>
                        </div>
                      </div>

                      {/* Right: Highlight + Button */}
                      <div className="flex flex-col md:flex-row items-start md:items-center gap-3 md:w-1/4 md:justify-end">
                        <span className="text-white/70 text-xs">
                          {plan.highlight}
                        </span>
                        <motion.button
                          onClick={() => handleGetStarted(plan.name)}
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                          className={`px-6 py-2.5 rounded-lg font-semibold text-sm transition-all duration-300 flex items-center gap-2 ${
                            plan.popular
                              ? "bg-dineai-yellow text-blue-900 hover:bg-yellow-300 shadow-lg hover:shadow-xl"
                              : "bg-white/20 text-white hover:bg-white/30 border border-white/30"
                          }`}
                        >
                          Get Started
                          <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                        </motion.button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Usage Fees Note */}
            <motion.div
              variants={itemVariants}
              className="mt-8 text-center"
            >
              <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 text-white/60 text-sm mb-3">
                <span>Overage: <span className="text-white/90 font-medium">10¢/min</span></span>
                <span>Convenience Fee: <span className="text-white/90 font-medium">99¢ - $1.99/order</span></span>
                <span>Setup: <span className="text-white/90 font-medium">$500 one-time</span></span>
              </div>
              <p className="text-white/40 text-xs">
                Convenience fee (99¢ under $30, $1.49 for $30-75, $1.99 over $75) charged to customer. Stripe processing (2.9% + 30¢) applies.
              </p>
            </motion.div>

            {/* All Plans Include Section */}
            <motion.div
              variants={itemVariants}
              className="mt-12 bg-white/5 backdrop-blur-md rounded-2xl p-8 border border-white/10"
            >
              <h3 className="text-xl font-bold text-white text-center mb-6 flex items-center justify-center gap-2">
                <Gift className="w-5 h-5 text-dineai-yellow" />
                All Plans Include
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {sharedFeatures.map((feature, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    className="flex items-center gap-2 text-white/80"
                  >
                    <Check className="w-4 h-4 text-dineai-yellow flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Integrations Section */}
            <motion.div
              variants={itemVariants}
              className="mt-12"
            >
              <h3 className="text-xl font-bold text-white text-center mb-8">
                Seamless Integrations
              </h3>
              <div className="grid grid-cols-3 gap-4 max-w-2xl mx-auto">
                {/* Stripe - Active */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white rounded-xl p-6 shadow-lg flex flex-col items-center justify-center gap-3"
                >
                  <img
                    src="/images/integrations/stripe_logo.png"
                    alt="Stripe"
                    className="h-10 object-contain"
                  />
                  <span className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></span>
                    Active
                  </span>
                </motion.div>

                {/* Clover - Coming Soon */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/90 rounded-xl p-6 shadow-lg flex flex-col items-center justify-center gap-3"
                >
                  <img
                    src="/images/integrations/clover_logo.png"
                    alt="Clover POS"
                    className="h-10 object-contain grayscale opacity-60"
                  />
                  <span className="text-xs text-gray-500 font-medium">Coming Soon</span>
                </motion.div>

                {/* Square - Coming Soon */}
                <motion.div
                  whileHover={{ scale: 1.05 }}
                  className="bg-white/90 rounded-xl p-6 shadow-lg flex flex-col items-center justify-center gap-3"
                >
                  <img
                    src="/images/integrations/square-logo.png"
                    alt="Square POS"
                    className="h-10 object-contain grayscale opacity-60"
                  />
                  <span className="text-xs text-gray-500 font-medium">Coming Soon</span>
                </motion.div>
              </div>
            </motion.div>
          </div>
        </motion.section>

        {/* Testimonials */}
        <motion.section
          className="py-16 px-4 bg-[#1f4ed1]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-6xl mx-auto">
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-center mb-12 text-white"
            >
              What Restaurant Owners Say
            </motion.h2>

            <div className="grid md:grid-cols-3 gap-8">
              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-dineai-yellow text-dineai-yellow" />
                  ))}
                </div>
                <p className="text-white/90 mb-4">
                  "Virtual Host handles our lunch rush perfectly. Customers love how natural the conversations feel, and we never miss a phone order anymore."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-dineai-yellow to-yellow-600 flex items-center justify-center text-blue-900 font-bold">
                    MR
                  </div>
                  <div>
                    <p className="font-semibold text-white">Maria Rodriguez</p>
                    <p className="text-sm text-white/70">Taco Haven, Austin TX</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-dineai-yellow text-dineai-yellow" />
                  ))}
                </div>
                <p className="text-white/90 mb-4">
                  "The payment processing integration is brilliant. Customers pay over the phone and orders go straight to our kitchen - saves us so much time."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-dineai-yellow to-yellow-600 flex items-center justify-center text-blue-900 font-bold">
                    JC
                  </div>
                  <div>
                    <p className="font-semibold text-white">James Chen</p>
                    <p className="text-sm text-white/70">Dragon Wok, San Francisco</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-dineai-yellow text-dineai-yellow" />
                  ))}
                </div>
                <p className="text-white/90 mb-4">
                  "We upgraded to Premium and it paid for itself in week one. The usage tracking helps us forecast busy periods and staff accordingly."
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-dineai-yellow to-yellow-600 flex items-center justify-center text-blue-900 font-bold">
                    SP
                  </div>
                  <div>
                    <p className="font-semibold text-white">Sarah Patel</p>
                    <p className="text-sm text-white/70">Curry Palace, Chicago</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </motion.section>

        {/* FAQ Section */}
        <motion.section
          className="py-16 px-4 bg-[#2256e5]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-4xl mx-auto">
            <motion.h2
              variants={itemVariants}
              className="text-4xl font-bold text-center mb-12 text-white"
            >
              Frequently Asked Questions
            </motion.h2>

            <div className="space-y-4">
              <FAQItem
                question="What is Virtual Host?"
                answer="Virtual Host is an AI-powered phone assistant that answers calls, takes orders, and processes payments 24/7. It uses advanced emotional voice AI to provide natural conversations with customers, handles complex orders, and integrates with Stripe for secure payment processing."
              />

              <FAQItem
                question="How does usage-based pricing work?"
                answer="Each plan includes a base monthly fee plus included minutes. For example, Starter plan is $149/month with 1,000 included minutes. Additional minutes are charged at $0.10/min and completed orders incur a tiered convenience fee (99¢ under $30, $1.49 for $30-75, $1.99 over $75). Your first month includes a one-time $500 setup fee for system configuration."
              />

              <FAQItem
                question="Can I track my usage in real-time?"
                answer="Yes! Your dashboard shows real-time usage tracking including minutes used this cycle, calls received, orders completed, revenue generated, and projected costs. You'll always know exactly where you stand before your monthly bill arrives."
              />

              <FAQItem
                question="What happens if I exceed my included minutes?"
                answer="No problem! You're automatically charged $0.10 per additional minute. There are no service interruptions - your Virtual Host keeps working seamlessly. Your dashboard shows projected overage charges in real-time so there are no surprises."
              />

              <FAQItem
                question="How does payment processing work?"
                answer="Virtual Host integrates with Stripe to securely collect credit card payments over the phone. When customers place orders, they can pay immediately during the call. Payment confirmation is instant and orders are sent directly to your kitchen."
              />

              <FAQItem
                question="Can I upgrade or downgrade my plan?"
                answer="Yes! You can upgrade or downgrade at any time. Upgrades take effect immediately with prorated charges. Downgrades take effect at the start of your next billing cycle. Your included minutes will adjust to match your new plan."
              />

              <FAQItem
                question="Is there a setup fee?"
                answer="Yes, there's a one-time $500 setup fee charged with your first invoice. This covers system configuration, menu integration, voice training, and phone number setup. No setup fees for subsequent months."
              />

              <FAQItem
                question="What's the difference between plans?"
                answer="Plans differ in included minutes and features. Starter ($149) includes 1,000 minutes, Basic ($250) includes 2,000 minutes, Standard ($350) includes 2,750 minutes with priority support, and Premium ($600) includes 5,000 minutes with dedicated account management and advanced analytics. All plans have the same $0.10/min overage rate and tiered convenience fee (99¢-$1.99 based on order size)."
              />
            </div>
          </div>
        </motion.section>

        {/* CTA Section - Two Column */}
        <motion.section
          id="sms-terms"
          className="py-20 px-4 bg-[#1f4ed1]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-center">
              {/* Left Column - CTA */}
              <motion.div variants={itemVariants} className="text-center md:text-left">
                <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white">
                  Ready to Never Miss a Call Again?
                </h2>
                <p className="text-xl text-white/90 mb-8">
                  Join restaurants already using AI to handle phone orders 24/7
                </p>
                <button
                  onClick={() => navigate("/dashboard")}
                  className="bg-dineai-yellow text-blue-900 px-8 py-4 rounded-lg font-bold text-lg hover:bg-yellow-300 transition-all duration-300 shadow-xl hover:shadow-2xl hover:scale-105"
                >
                  Get Started Today
                </button>
              </motion.div>

              {/* Right Column - SMS Terms */}
              <motion.div
                variants={itemVariants}
                className="bg-white/10 backdrop-blur-md rounded-xl p-6 border border-white/20"
              >
                <h3 className="text-lg font-bold mb-3 text-white flex items-center gap-2">
                  <svg className="w-5 h-5 text-dineai-yellow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                  SMS Order Confirmations
                </h3>
                <p className="text-white/80 text-sm mb-3">
                  Customers can opt-in to receive order summary and payment link via SMS.
                </p>
                <ul className="text-white/70 text-xs space-y-1 mb-3">
                  <li>• <strong className="text-white/90">Verbal Consent:</strong> Asked during call, "yes" = consent</li>
                  <li>• <strong className="text-white/90">Frequency:</strong> 1 SMS per order (no marketing)</li>
                  <li>• <strong className="text-white/90">Opt-Out:</strong> Reply STOP or decline on call</li>
                  <li>• <strong className="text-white/90">Rates:</strong> Standard carrier rates apply</li>
                </ul>
                <a href="/privacy" className="text-dineai-yellow hover:underline text-sm">
                  View SMS Terms & Privacy Policy →
                </a>
              </motion.div>
            </div>
          </div>
        </motion.section>

        <Footer />

        {/* Audio Player Modal for Virtual Host Demo */}
        <AudioPlayerModal
          isOpen={isAudioModalOpen}
          onClose={() => setIsAudioModalOpen(false)}
          audioSrc="/audio/virtual-host-demo.wav"
          title="AI Virtual Host Demo"
        />
      </div>
    </>
  );
};

// FAQ Item Component
const FAQItem: React.FC<{ question: string; answer: string }> = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <motion.div
      className="bg-white/10 backdrop-blur-md rounded-xl border border-white/20 overflow-hidden"
      initial={false}
    >
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-white/5 transition-colors"
      >
        <span className="font-semibold text-white">{question}</span>
        <motion.div
          animate={{ rotate: isOpen ? 180 : 0 }}
          transition={{ duration: 0.3 }}
          className="text-dineai-yellow"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </motion.div>
      </button>

      <motion.div
        initial={false}
        animate={{
          height: isOpen ? "auto" : 0,
          opacity: isOpen ? 1 : 0
        }}
        transition={{ duration: 0.3 }}
        className="overflow-hidden"
      >
        <div className="px-6 pb-4 text-white/80">
          {answer}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default VirtualHost;
